/**
 * Announcement Edit Dialog Component
 *
 * Form dialog for creating and editing announcements.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  Alert,
} from '@mui/material';
import {
  useCreateAnnouncementMutation,
  useUpdateAnnouncementMutation,
} from '../../api/announcementsApi';
import {
  MONTH_OPTIONS,
  getDayOptions,
  type Announcement,
  type AnnouncementFormData,
} from '../../types/announcements';

interface AnnouncementEditDialogProps {
  open: boolean;
  onClose: () => void;
  studioId: string;
  announcement: Announcement | null;
}

const AnnouncementEditDialog: React.FC<AnnouncementEditDialogProps> = ({
  open,
  onClose,
  studioId,
  announcement,
}) => {
  const [createAnnouncement, { isLoading: isCreating }] =
    useCreateAnnouncementMutation();
  const [updateAnnouncement, { isLoading: isUpdating }] =
    useUpdateAnnouncementMutation();

  const [formData, setFormData] = useState<AnnouncementFormData>({
    title: '',
    message: '',
    start_month: 1,
    start_day: 1,
    end_month: 1,
    end_day: 31,
    repeat_yearly: true,
    is_active: true,
  });

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (announcement) {
      setFormData({
        title: announcement.title,
        message: announcement.message,
        start_month: announcement.start_month,
        start_day: announcement.start_day,
        end_month: announcement.end_month,
        end_day: announcement.end_day,
        repeat_yearly: announcement.repeat_yearly,
        is_active: announcement.is_active,
      });
    } else {
      setFormData({
        title: '',
        message: '',
        start_month: 1,
        start_day: 1,
        end_month: 1,
        end_day: 31,
        repeat_yearly: true,
        is_active: true,
      });
    }
    setError(null);
  }, [announcement, open]);

  const handleSubmit = async () => {
    if (!formData.message.trim()) {
      setError('Message is required');
      return;
    }

    try {
      if (announcement) {
        await updateAnnouncement({
          studioId,
          announcementId: announcement.id,
          data: formData,
        }).unwrap();
      } else {
        await createAnnouncement({
          studioId,
          data: formData,
        }).unwrap();
      }
      onClose();
    } catch (err) {
      setError('Failed to save announcement. Please try again.');
      console.error('Failed to save announcement:', err);
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {announcement ? 'Edit Announcement' : 'Add Announcement'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Title (Optional)"
          value={formData.title}
          onChange={e => setFormData({ ...formData, title: e.target.value })}
          sx={{ mt: 2, mb: 2 }}
        />

        <TextField
          fullWidth
          label="Message"
          value={formData.message}
          onChange={e => setFormData({ ...formData, message: e.target.value })}
          multiline
          rows={4}
          required
          sx={{ mb: 2 }}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
            mb: 2,
          }}
        >
          <FormControl fullWidth>
            <InputLabel>Start Month</InputLabel>
            <Select
              value={formData.start_month}
              label="Start Month"
              onChange={e =>
                setFormData({
                  ...formData,
                  start_month: Number(e.target.value),
                })
              }
            >
              {MONTH_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Start Day</InputLabel>
            <Select
              value={formData.start_day}
              label="Start Day"
              onChange={e =>
                setFormData({ ...formData, start_day: Number(e.target.value) })
              }
            >
              {getDayOptions(formData.start_month).map(day => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 2,
            mb: 2,
          }}
        >
          <FormControl fullWidth>
            <InputLabel>End Month</InputLabel>
            <Select
              value={formData.end_month}
              label="End Month"
              onChange={e =>
                setFormData({ ...formData, end_month: Number(e.target.value) })
              }
            >
              {MONTH_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>End Day</InputLabel>
            <Select
              value={formData.end_day}
              label="End Day"
              onChange={e =>
                setFormData({ ...formData, end_day: Number(e.target.value) })
              }
            >
              {getDayOptions(formData.end_month).map(day => (
                <MenuItem key={day} value={day}>
                  {day}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={formData.repeat_yearly}
                onChange={e =>
                  setFormData({ ...formData, repeat_yearly: e.target.checked })
                }
              />
            }
            label="Repeat Yearly"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={e =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
              />
            }
            label="Active"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={isLoading}>
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnnouncementEditDialog;
